import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { PostgrestBuilder } from '@supabase/postgrest-js';
import { toast } from 'sonner';

type SupabaseMutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, Error, TVariables>,
  'mutationFn'
> & {
  invalidateQueries?: string[];
  successMessage?: string;
  errorMessage?: string;
};

export function useSupabaseMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<{ data: TData | null; error: any }>,
  {
    invalidateQueries = [],
    successMessage = 'Operação realizada com sucesso!',
    errorMessage = 'Erro ao realizar operação',
    ...options
  }: SupabaseMutationOptions<TData, TVariables> = {}
) {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables>({
    mutationFn: async (variables) => {
      try {
        const { data, error } = await mutationFn(variables);
        if (error) throw error;
        return data as TData;
      } catch (error: any) {
        console.error('Erro na mutação:', error);
        throw new Error(error.message || errorMessage);
      }
    },
    onSuccess: (data, variables, context) => {
      // Invalidar queries relacionadas
      invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey: ['supabase', queryKey] });
      });

      // Mostrar mensagem de sucesso
      toast.success(successMessage);

      // Chamar onSuccess personalizado se fornecido
      options.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Mostrar mensagem de erro
      toast.error(error.message || errorMessage);

      // Chamar onError personalizado se fornecido
      options.onError?.(error, variables, context);
    },
    ...options,
  });
} 